/**
 * MentionsPage - /crm/mentions (docs/plans/crm-report-builder-search-collab-plan.md A3.4 step 6 and
 * B3.4): every mention of me, read or not, and every reply to a note I wrote, newest first, 50 at a
 * time. All or Unread (remembered per device), Mark all read, and Load more.
 *
 * Reached from the Mentions lane's "See all" on Today and from quick search; CRMApp routes it. A
 * row opens the lead on that note (MentionRow, shared with the lane), and opening the lead marks
 * its mentions read (decision M4 a). An api that does not serve mentions (404, 5xx, no answer)
 * gets a quiet "not available" state instead of an error box.
 *
 * Nothing here polls: the page reads once per filter, and again on Refresh, Mark all read and
 * Load more. The unread count in the header is the shared nav counts' (the one count poll).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { AtSign, CheckCheck, Loader2 } from 'lucide-react';
import PageHeader, { RefreshButton } from '../components/PageHeader';
import SegmentedControl from '../components/filters/SegmentedControl';
import Alert from '../components/ui/Alert';
import EmptyState from '../../ui/EmptyState';
import { outlineBtnCls } from '../../ui/styles';
import { notifyStatsChanged, usePersistedState, useTeam } from '../lib';
import { useSharedNavCounts } from '../useNavCounts';
import { isMentionsUnavailable, listMentions, markMentionsRead, mentionErrorMessage } from './api';
import { MENTION_LIST_LIMIT_MAX } from './mentionLogic';
import { MentionRow } from './MentionsLane';
import type { MentionItem, MentionStatusFilter } from './types';

/** The per-device filter pref (crm.mentions.status); "all" until a person picks Unread. */
export const MENTIONS_FILTER_PREF = 'mentions.status';

const FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'unread', label: 'Unread' },
];

export default function MentionsPage() {
  const { byId } = useTeam();
  const nameOf = useCallback((id: string): string | null => byId.get(id)?.name ?? null, [byId]);
  const [storedFilter, setStoredFilter] = usePersistedState<string>(MENTIONS_FILTER_PREF, 'all');
  const status: MentionStatusFilter = storedFilter === 'unread' ? 'unread' : 'all';
  const unreadCount = useSharedNavCounts().mentions?.count ?? null;

  const [rows, setRows] = useState<MentionItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [marking, setMarking] = useState(false);
  const [markError, setMarkError] = useState<string | null>(null);
  // Each read takes a number; an answer that is no longer the newest read is dropped, so a slow
  // "All" answer can never land on top of the "Unread" list that replaced it.
  const readSeq = useRef(0);

  const load = useCallback(async () => {
    readSeq.current += 1;
    const mine = readSeq.current;
    setLoading(true);
    setError(null);
    try {
      const res = await listMentions({ status, limit: MENTION_LIST_LIMIT_MAX });
      if (mine !== readSeq.current) return;
      setRows(res.mentions);
      setHasMore(res.has_more);
      setUnavailable(false);
    } catch (e) {
      if (mine !== readSeq.current) return;
      // An api without mentions (or one that is down) gets the quiet state; anything else, after a
      // "Try again" from that state too, gets its sentence and Retry.
      const gone = isMentionsUnavailable(e);
      setUnavailable(gone);
      setError(gone ? null : mentionErrorMessage(e, "Couldn't load your mentions. Please try again."));
    } finally {
      if (mine === readSeq.current) setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    void load();
  }, [load]);

  const chooseFilter = (value: string) => {
    const next: MentionStatusFilter = value === 'unread' ? 'unread' : 'all';
    if (next === status) return;
    // Never show the other filter's rows under this one while the new read is on its way.
    setRows([]);
    setHasMore(false);
    setStoredFilter(next);
  };

  const loadMore = async () => {
    const last = rows[rows.length - 1];
    if (!last || loadingMore) return;
    const mine = readSeq.current;
    setLoadingMore(true);
    setError(null);
    try {
      const res = await listMentions({ status, limit: MENTION_LIST_LIMIT_MAX, before: last.created_at });
      if (mine !== readSeq.current) return;
      setRows((prev) => {
        const have = new Set(prev.map((r) => r.id));
        return [...prev, ...res.mentions.filter((r) => !have.has(r.id))];
      });
      setHasMore(res.has_more);
    } catch (e) {
      if (mine !== readSeq.current) return;
      setError(mentionErrorMessage(e, "Couldn't load more mentions. Please try again."));
    } finally {
      setLoadingMore(false);
    }
  };

  const markAll = async () => {
    if (marking) return;
    setMarking(true);
    setMarkError(null);
    try {
      await markMentionsRead({ all: true });
      // The Today badge, the lane and any mention card clear on the one count poll.
      notifyStatsChanged();
      await load();
    } catch (e) {
      setMarkError(mentionErrorMessage(e, "Couldn't mark your mentions read. Please try again."));
    } finally {
      setMarking(false);
    }
  };

  const anyUnread = rows.some((r) => !r.read_at) || (unreadCount ?? 0) > 0;

  if (unavailable) {
    return (
      <div className="space-y-4">
        <PageHeader icon={AtSign} title="Mentions" />
        <EmptyState
          variant="card"
          icon={AtSign}
          title="Mentions aren't available yet"
          hint="This part of the CRM is still being switched on, or couldn't be reached. Check back soon."
          action={
            <button type="button" onClick={() => void load()} className={outlineBtnCls()}>
              Try again
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        icon={AtSign}
        title="Mentions"
        subtitle={
          unreadCount && unreadCount > 0
            ? `Notes where a teammate named you, and replies to notes you wrote. ${unreadCount.toLocaleString()} unread.`
            : 'Notes where a teammate named you, and replies to notes you wrote.'
        }
        actions={
          <>
            <SegmentedControl label="Which mentions" value={status} onChange={chooseFilter} options={FILTER_OPTIONS} />
            <button
              type="button"
              onClick={markAll}
              disabled={marking || !anyUnread}
              className={`${outlineBtnCls()} disabled:cursor-not-allowed disabled:opacity-50`}
            >
              {marking
                ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                : <CheckCheck className="h-4 w-4" aria-hidden="true" />}
              Mark all read
            </button>
            <RefreshButton onClick={() => void load()} loading={loading} label="Refresh mentions" />
          </>
        }
      />

      {markError && (
        <Alert tone="error" size="sm">
          {markError}
        </Alert>
      )}

      {error && (
        <Alert
          tone="error"
          icon={false}
          actions={
            <button type="button" onClick={() => void load()} className="font-medium underline hover:no-underline">
              Retry
            </button>
          }
        >
          {error}
        </Alert>
      )}

      {loading && rows.length === 0 ? (
        <div className="flex justify-center py-16" role="status" aria-label="Loading mentions">
          <Loader2 className="h-6 w-6 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
        </div>
      ) : rows.length === 0 ? (
        error ? null : (
          <EmptyState
            variant="card"
            icon={AtSign}
            title={status === 'unread' ? 'No unread mentions' : 'No mentions yet'}
            hint={
              status === 'unread'
                ? "You're all caught up. Choose All to see the ones you have read."
                : 'When a teammate types @ and your name in a note, or replies to a note you wrote, it shows up here and on Today.'
            }
          />
        )
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <div className="divide-y divide-gray-100 px-4 dark:divide-gray-700">
            {rows.map((item) => (
              <MentionRow key={item.id} item={item} nameOf={nameOf} markUnread />
            ))}
          </div>
          {hasMore && (
            <div className="flex justify-center border-t border-gray-100 px-4 py-3 dark:border-gray-700">
              <button type="button" onClick={loadMore} disabled={loadingMore} className={outlineBtnCls()}>
                {loadingMore && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                Load more
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
